import { useQuery } from "@tanstack/react-query";
import { searchEntities } from "../api";
import type { ShallowEntity, ShallowSearchResult } from "../types";
import {
  SPECIAL_PERSON_ATTRIBUTE,
  SPECIAL_VEHICLE_ATTRIBUTE,
} from "./EntitySearchInput";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

type CardFacts = ShallowSearchResult["facts"];

// Under "rms", so the blanket `queryKey[0] === "rms"` invalidation that every
// entity write already does (api.ts) refreshes a card's facts too.
const cardHydrationKey = (entityId: string) =>
  ["rms", "card-hydration", entityId] as const;

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * The header facts a record card shows under its name, for a card whose search
 * result did not already carry them.
 *
 * The cache is react-query's, not a module-level `Map`: it is scoped to the
 * `QueryClient`, dropped with it, and invalidated by the same calls as the rest
 * of the RMS. Leaving the page withdraws the request (#12187).
 */
export const useCardHydration = (
  entity: ShallowEntity,
  attributes: string[]
): { facts: CardFacts; loading: boolean } => {
  // Validate that only expected attributes are passed
  const allowedAttributes = [
    SPECIAL_PERSON_ATTRIBUTE,
    SPECIAL_VEHICLE_ATTRIBUTE,
  ];
  const invalidAttributes = attributes.filter(
    (attr) => !allowedAttributes.includes(attr)
  );
  if (invalidAttributes.length > 0) {
    throw new Error(
      `Invalid attributes passed to useCardHydration: ${invalidAttributes.join(", ")}. Only ${allowedAttributes.join(", ")} are supported.`
    );
  }

  const enabled = attributes.length > 0;
  const query = useQuery({
    queryKey: cardHydrationKey(entity.id),
    // Taking `signal` is what makes react-query abort it when the last card
    // showing this entity unmounts; the batcher then drops the id.
    queryFn: ({ signal }) => hydrateInBatch(entity.id, signal),
    enabled,
    // Facts change only through a write, and every write invalidates "rms".
    staleTime: Infinity,
    // A card is a decoration on a row: a failed read shows the bare name
    // rather than retrying behind it.
    retry: false,
  });

  return {
    facts: enabled ? (query.data ?? null) : null,
    loading: enabled && query.isPending,
  };
};

// ============================================================================
// BATCHING
// ============================================================================

/** How long a card waits for its neighbours before one request goes out for all of them. */
export const HYDRATION_BATCH_WINDOW_MS = 200;

type Waiter = {
  resolve: (facts: CardFacts) => void;
  reject: (error: unknown) => void;
};

// A list of cards mounts in one commit, and one request for forty ids is the
// point of this file. This queue is the only module-level state, and it is
// transient by construction: it holds an id only between a card mounting and
// the window closing, and the timer exists only while it is non-empty. An
// import starts nothing.
const waiting = new Map<string, Set<Waiter>>();
let windowTimer: ProductTimer | undefined;

function hydrateInBatch(
  entityId: string,
  signal: AbortSignal
): Promise<CardFacts> {
  return new Promise<CardFacts>((resolve, reject) => {
    const waiter: Waiter = { resolve, reject };
    const waiters = waiting.get(entityId) ?? new Set<Waiter>();
    waiters.add(waiter);
    waiting.set(entityId, waiters);
    windowTimer ??= setProductTimeout(sendBatch, HYDRATION_BATCH_WINDOW_MS);

    signal.addEventListener(
      "abort",
      () => {
        waiters.delete(waiter);
        if (waiters.size === 0) waiting.delete(entityId);
        if (waiting.size === 0) {
          clearProductTimeout(windowTimer);
          windowTimer = undefined;
        }
        reject(signal.reason);
      },
      { once: true }
    );
  });
}

async function sendBatch(): Promise<void> {
  windowTimer = undefined;
  const batch = Array.from(waiting.entries());
  waiting.clear();
  if (batch.length === 0) return;

  try {
    const response = await searchEntities(
      "",
      undefined,
      [SPECIAL_PERSON_ATTRIBUTE, SPECIAL_VEHICLE_ATTRIBUTE],
      batch.map(([entityId]) => entityId)
    );
    const factsById = new Map<string, CardFacts>();
    response.results.forEach((result) => {
      if (result.entity && result.entity.id) {
        factsById.set(result.entity.id, result.facts);
      } else {
        console.warn("Invalid entity in hydration results:", result);
      }
    });
    // An entity the search did not return has no facts, which is an answer.
    batch.forEach(([entityId, waiters]) => {
      waiters.forEach((w) => w.resolve(factsById.get(entityId) ?? null));
    });
  } catch (error) {
    console.error("Failed to fetch hydration data:", error);
    batch.forEach(([, waiters]) => waiters.forEach((w) => w.reject(error)));
  }
}
